import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { socketIo } from 'context/socket';
import { CONSTANTS } from 'config/constants';
import { BatchOrderFailure } from 'models/order';

// Mirrors DeliveryNoteBatchSocketEvent in shared-denthub-code (the frontend has no
// shared import); socket-server emits both events.
export const DELIVERY_NOTES_BATCH_PDF_EVENT = 'DELIVERY_NOTES_BATCH_PDF';
export const DELIVERY_NOTES_BATCH_PROGRESS_EVENT = 'DELIVERY_NOTES_BATCH_PROGRESS';

// Compiling 50 notes takes a couple of minutes at most; past this the user is told
// the notes are still on each order rather than left with a spinner.
const BATCH_TIMEOUT_MS = 10 * 60 * 1000;

export type DeliveryNoteBatchEvent = {
    batchId: string;
    total: number;
    completedCount: number;
    failedOrders: { order: string; orderId: string; errorMessage: string }[];
    deliveryNotesPdfUrl?: string;
    sessionStr: string;
};

export type DeliveryNoteBatchProgressEvent = {
    batchId: string;
    total: number;
    completedCount: number;
    failedCount: number;
    sessionStr: string;
};

export type DeliveryNoteBatchState =
    | { status: 'idle' }
    | { status: 'pending'; batchId: string; total: number; processedCount: number }
    | { status: 'ready'; batchId: string; event: DeliveryNoteBatchEvent }
    | { status: 'timeout'; batchId: string };

/**
 * DD-3463: follows one batch action's compiled delivery-note PDF. `start` is
 * called with the batch id the mutation returned; the single socket event the
 * backend sends when every note is processed moves the state to `ready`.
 */
export const useDeliveryNoteBatch = () => {
    const socket = useContext(socketIo.getSocketContext());
    const [state, setState] = useState<DeliveryNoteBatchState>({ status: 'idle' });
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const clearTimer = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
        }
    };

    const start = useCallback((batchId: string, total: number) => {
        clearTimer();
        setState({ status: 'pending', batchId, total, processedCount: 0 });
        timeoutRef.current = setTimeout(() => {
            setState((current) =>
                current.status === 'pending' && current.batchId === batchId ? { status: 'timeout', batchId } : current,
            );
        }, BATCH_TIMEOUT_MS);
    }, []);

    const dismiss = useCallback(() => {
        clearTimer();
        setState({ status: 'idle' });
    }, []);

    useEffect(() => {
        if (!socket) {
            return undefined;
        }
        const handler = (event: DeliveryNoteBatchEvent) => {
            try {
                const storedSessionStr = sessionStorage.getItem(CONSTANTS.SOCKET_SESSION);
                if (event.sessionStr !== storedSessionStr) {
                    return;
                }
                setState((current) => {
                    if (current.status === 'idle' || current.batchId !== event.batchId) {
                        return current;
                    }
                    clearTimer();
                    return { status: 'ready', batchId: event.batchId, event };
                });
            } catch (error) {
                console.error(error);
            }
        };
        // DD-3684: non-final per-note progress. The three Kafka partitions deliver out of
        // order, so the count only ever moves forward (max), never back.
        const progressHandler = (event: DeliveryNoteBatchProgressEvent) => {
            try {
                const storedSessionStr = sessionStorage.getItem(CONSTANTS.SOCKET_SESSION);
                if (event.sessionStr !== storedSessionStr) {
                    return;
                }
                setState((current) => {
                    if (current.status !== 'pending' || current.batchId !== event.batchId) {
                        return current;
                    }
                    const processedCount = Math.max(current.processedCount, event.completedCount + event.failedCount);
                    if (processedCount === current.processedCount) {
                        return current;
                    }
                    return { ...current, processedCount };
                });
            } catch (error) {
                console.error(error);
            }
        };
        socket.on(DELIVERY_NOTES_BATCH_PDF_EVENT, handler);
        socket.on(DELIVERY_NOTES_BATCH_PROGRESS_EVENT, progressHandler);
        return () => {
            socket.off(DELIVERY_NOTES_BATCH_PDF_EVENT, handler);
            socket.off(DELIVERY_NOTES_BATCH_PROGRESS_EVENT, progressHandler);
        };
    }, [socket]);

    useEffect(() => () => clearTimer(), []);

    return { state, start, dismiss };
};

/** The failed orders of a batch, as the text shown next to the count. */
export const describeFailedOrders = (failed: Pick<BatchOrderFailure, 'orderId'>[]): string =>
    failed.map((order) => order.orderId).join(', ');
